/**
 * 2.0 chat (P9) — a coach card's link, aimed at the room with THAT coach called.
 *
 * With `chat_2_0_v1` on, `/chat` is the one room and the room reads `?coach=<CoachId>` (an unknown id calls
 * nobody). Desktop Today's `CoachCard` prints `message.cta.href`, and the rules engine writes a bare `/v2/chat`
 * for it (`lib/coachInsightRules.ts`), so a tap on "Ask Onai" opened the room with nobody called. This adds the
 * coach the card belongs to, as the room's long id (`lib/coaches.ts`), and touches nothing else:
 *
 *   - a bare `/v2/chat` or `/chat`           -> the same path with `?coach=<long id>`
 *   - the demo fixtures' `?coach=<short id>` -> the same path with the long id (the short id is not a room id)
 *   - any other href (`?draftWeek=N`, `&run=`, an id already long, a non-chat link) is returned as it was.
 *
 * The caller applies it only with the flag on; flag off the card keeps its href untouched. (A coach card on the
 * phone is `CoachStories`, which already carries its coach in router state and needs no change.)
 */
import { COACHES, type CoachId as RoomCoachId } from '../../lib/coaches';
import type { CoachId as ShortCoachId, CoachMessage } from '../../lib/coachMessages';

/** The room's long id for a card's short id ('recovery' -> 'recovery-nutrition'), matched on the `--c-*` token as `coachFromInsight` does. */
function roomIdFor(short: ShortCoachId | string): RoomCoachId | undefined {
  return COACHES.find((c) => c.colorVar === `var(--c-${short})`)?.id;
}

const BARE_CHAT = /^\/(?:v2\/)?chat$/;
const SHORT_COACH_CHAT = /^(\/(?:v2\/)?chat)\?coach=([a-z]+)$/;

export function roomCoachHref(href: string, coach: ShortCoachId): string {
  if (BARE_CHAT.test(href)) {
    const id = roomIdFor(coach);
    return id ? `${href}?coach=${id}` : href;
  }
  const m = SHORT_COACH_CHAT.exec(href);
  if (m) {
    const id = roomIdFor(m[2]);
    return id ? `${m[1]}?coach=${id}` : href;
  }
  return href;
}

/** The same message with its link aimed at the room; the very same object when nothing changes. */
export function roomCoachMessage(message: CoachMessage): CoachMessage {
  const href = roomCoachHref(message.cta.href, message.coach);
  return href === message.cta.href ? message : { ...message, cta: { ...message.cta, href } };
}
